import type { LiveBreakWidgetCopy } from "@loot-os/api-contract";
import chevronRightIcon from "@loot-os/icons/icons/chevron-right";
import usersIcon from "@loot-os/icons/icons/users";

import { Avatar } from "../../core/Avatar.js";
import { Icon } from "../../core/Icon.js";
import { Text } from "../../core/Text.js";
import { AppLink } from "../../foundations/AppLink.js";
import { cn } from "../../foundations/cn.js";
import { FOCUS_RING } from "../../foundations/interaction.js";
import { useLayout } from "../../foundations/themeContext.js";
import { LiveBreakStatusBadge } from "./LiveBreakStatusBadge.js";
import {
  type LiveBreakWidgetHosting,
  type LiveBreakWidgetQueue,
  widgetSurface,
} from "./liveBreakWidget.js";

export type LiveBreakWidgetReturnRowProps = {
  words: LiveBreakWidgetCopy;
  className?: string;
} & (
  | {
      /** Back to the shopper's own open live break, with how many wait in it. */
      kind: "hosting";
      row: LiveBreakWidgetHosting;
    }
  | {
      /** Back to the live break the shopper waits soonest in, with their place. */
      kind: "queue";
      row: LiveBreakWidgetQueue;
    }
);

/**
 * A Return row: one link back to a live break the shopper hosts ("Hosting", the count waiting) or
 * waits in ("In queue", their place), its host's avatar ringed while they take part. An entry that
 * opens next says so as the queue does ("Up next", the NEXT badge), never as one more place. The
 * figure is plain text, so only the row and its chevron read as something to press.
 */
export function LiveBreakWidgetReturnRow({
  words,
  className,
  ...props
}: LiveBreakWidgetReturnRowProps) {
  const layout = useLayout();
  const { row } = props;
  const copy = props.kind === "hosting" ? words.hosting : words.queue;
  const upNext = props.kind === "queue" && props.row.position === 1;
  return (
    <AppLink
      href={row.href}
      className={cn(
        "flex w-full items-center gap-2 rounded-xl py-1.5 pr-2 pl-3",
        "max-md:py-1 max-md:pr-1.5 max-md:pl-2.5 hover:bg-bg-hover",
        widgetSurface(layout.chrome),
        FOCUS_RING,
        className,
      )}
    >
      <span className="relative shrink-0">
        <Avatar
          src={row.hostAvatarSrc}
          name={row.hostName}
          size="md"
          decorative
          outlined
          className="ring-2 ring-status-danger max-md:size-9"
        />
        <span
          aria-hidden
          className="absolute right-0 bottom-0 size-2.5 rounded-pill border-2 border-bg-surface bg-status-danger"
        />
      </span>
      <span className="flex min-w-32 flex-1 flex-col">
        <Text as="span" variant="body-b3-strong" tone={upNext ? "primary" : "secondary"}>
          {upNext ? words.queue.upNext : copy.eyebrow}
        </Text>
        <Text as="span" variant="body-b2-strong" tone="primary">
          {copy.title}
        </Text>
      </span>
      <span className="ml-4 flex h-8 shrink-0 items-center gap-1.5 text-text-primary">
        {props.kind === "hosting" ? (
          <>
            <Icon icon={usersIcon} size={16} className="text-text-tertiary" />
            <Text as="span" variant="body-b1-strong">
              {words.hosting.count(props.row.queuedCount)}
            </Text>
            <span className="sr-only">{words.hosting.countLabel}</span>
          </>
        ) : upNext ? (
          <LiveBreakStatusBadge status="next" label={words.queue.next} />
        ) : (
          <Text as="span" variant="body-b1-strong" className="font-mono tabular-nums">
            {words.queue.place(props.row.position)}
          </Text>
        )}
      </span>
      <span className="flex size-8 shrink-0 items-center justify-center text-text-secondary">
        <Icon icon={chevronRightIcon} size={16} />
      </span>
      {props.kind === "queue" && <span className="sr-only">{words.queue.host(row.hostName)}</span>}
    </AppLink>
  );
}
